'use client';

import { useEffect } from 'react';

function scrollToHash(rawHash: string) {
  if (!rawHash) return;
  const hash = rawHash.replace(/^#/, '');
  if (!hash) return;
  const target = document.getElementById(hash) ?? document.querySelector(`[data-hash='${hash}']`);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function handleAnchorNavigation(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  const anchor = target?.closest?.('a');
  if (!anchor) return;
  if (anchor.target && anchor.target !== '_self') return;
  if (anchor.hasAttribute('download')) return;

  const href = anchor.getAttribute('href');
  if (!href) return;

  // Pure hash link (#section)
  if (href.startsWith('#')) {
    event.preventDefault();
    const hash = href;
    history.pushState(null, '', `${window.location.pathname}${hash}`);
    scrollToHash(hash);
    return;
  }

  // Link with same pathname but hash (e.g. /fr/#join)
  if (href.includes('#')) {
    const url = new URL(href, window.location.origin);
    const targetExists = document.getElementById(url.hash.replace('#', ''));

    // Handle same-path hashes and locale/root mismatches (e.g., href=\"/#join\" while on \"/en\")
    if (url.pathname === window.location.pathname || targetExists) {
      event.preventDefault();
      history.pushState(null, '', `${window.location.pathname}${url.hash}`);
      scrollToHash(url.hash);
      return;
    }
  }
}

export function HashScrollManager() {
  useEffect(() => {
    const onHashChange = () => {
      scrollToHash(window.location.hash);
    };

    onHashChange();
    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('click', handleAnchorNavigation);

    return () => {
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('click', handleAnchorNavigation);
    };
  }, []);

  return null;
}
